import { useEffect, useState } from "react";
import api, { formatEUR, formatDate, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Building,
  Landmark,
  Plus,
  Search,
  RefreshCw,
  Trash2,
  ArrowDownRight,
  ArrowUpRight,
  Loader2,
  CheckCircle2,
  ShieldCheck,
  Wallet,
  TrendingUp,
  TrendingDown,
  Link2,
} from "lucide-react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";

const CAT_COLORS = [
  "#2563EB", "#F59E0B", "#10B981", "#EF4444", "#8B5CF6",
  "#EC4899", "#06B6D4", "#F97316", "#84CC16", "#6366F1",
];

export default function BancoPage() {
  const [connections, setConnections] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectOpen, setConnectOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [selectedInst, setSelectedInst] = useState("");
  const [filter, setFilter] = useState("");
  const [filterCat, setFilterCat] = useState("todas");
  const [reconciling, setReconciling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, i, s, t] = await Promise.all([
        api.get("/banking/connections"),
        api.get("/banking/institutions"),
        api.get("/banking/summary"),
        api.get("/banking/transactions"),
      ]);
      setConnections(c.data);
      setInstitutions(i.data.institutions);
      setSummary(s.data);
      setTransactions(t.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const connect = async () => {
    if (!selectedInst) {
      toast.error("Selecciona un banco");
      return;
    }
    setConnecting(true);
    try {
      // Simulate PSD2 consent screen delay
      await new Promise((r) => setTimeout(r, 1800));
      const { data } = await api.post("/banking/connect", {
        institution_id: selectedInst,
      });
      toast.success(
        `Banco conectado. ${data.transactions_count} movimientos importados${
          data.auto_reconciled > 0 ? ` · ${data.auto_reconciled} facturas conciliadas automáticamente` : ""
        }`,
      );
      setConnectOpen(false);
      setSelectedInst("");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async (id, name) => {
    if (!window.confirm(`¿Desconectar ${name}? Se eliminarán todos los movimientos importados.`)) return;
    await api.delete(`/banking/connections/${id}`);
    toast.success("Banco desconectado");
    load();
  };

  const refresh = async (id) => {
    try {
      const { data } = await api.post(`/banking/refresh/${id}`);
      toast.success(`${data.new_transactions} nuevos movimientos`);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const reconcile = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post("/banking/reconcile");
      if (data.matched > 0) {
        toast.success(`${data.matched} factura${data.matched === 1 ? "" : "s"} conciliada${data.matched === 1 ? "" : "s"}`);
      } else {
        toast.info("No se encontraron movimientos que coincidan con facturas pendientes");
      }
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setReconciling(false);
    }
  };

  const filtered = transactions.filter((t) => {
    if (filterCat !== "todas" && t.category !== filterCat) return false;
    if (filter && !t.description.toLowerCase().includes(filter.toLowerCase())) return false;
    return true;
  });

  const categoriesList = Array.from(new Set(transactions.map((t) => t.category).filter(Boolean)));

  if (loading) {
    return <div className="text-slate-500">Cargando datos bancarios...</div>;
  }

  // No connections — empty state
  if (connections.length === 0) {
    return (
      <div className="space-y-6" data-testid="banco-page">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">
            Banco
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Conecta tu banco para ver saldo, movimientos y conciliar facturas automáticamente.
          </p>
        </div>

        <Card className="p-10 text-center bg-gradient-to-br from-blue-50 to-white border-blue-100">
          <div className="w-16 h-16 rounded-2xl bg-blue-600 grid place-items-center mx-auto mb-4">
            <Landmark className="w-8 h-8 text-white" />
          </div>
          <h2 className="font-display text-2xl font-bold text-slate-900">
            Conecta tu primer banco
          </h2>
          <p className="text-slate-600 mt-2 max-w-md mx-auto">
            Consulta el saldo, revisa movimientos y marca facturas como pagadas
            automáticamente cuando entre el dinero.
          </p>
          <div className="mt-6 flex items-center justify-center gap-6 text-xs text-slate-500 flex-wrap">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-emerald-600" /> Seguro (PSD2)
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Sólo lectura
            </span>
            <span className="flex items-center gap-1.5">
              <RefreshCw className="w-4 h-4 text-emerald-600" /> Sincronización diaria
            </span>
          </div>
          <Button
            onClick={() => setConnectOpen(true)}
            className="mt-8 bg-blue-600 hover:bg-blue-700 h-11 px-8"
            data-testid="empty-connect-bank-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Conectar mi banco
          </Button>
          <div className="mt-6 text-[11px] text-slate-500 max-w-md mx-auto">
            <b>Modo demo activado</b>: los datos son de ejemplo para que veas cómo funciona. Cuando conectes tu banco real, verás tus movimientos reales.
          </div>
        </Card>

        <ConnectDialog
          open={connectOpen}
          onOpenChange={setConnectOpen}
          institutions={institutions}
          selectedInst={selectedInst}
          setSelectedInst={setSelectedInst}
          onConnect={connect}
          connecting={connecting}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="banco-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">
            Banco
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {connections.length} banco{connections.length === 1 ? "" : "s"} conectado{connections.length === 1 ? "" : "s"} · Última actualización hace unos minutos
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={reconcile}
            disabled={reconciling}
            data-testid="reconcile-btn"
          >
            {reconciling ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Link2 className="w-4 h-4 mr-1" />
            )}
            Conciliar facturas
          </Button>
          <Button
            onClick={() => setConnectOpen(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="connect-bank-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Añadir banco
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-5 bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0 stat-card">
          <div className="w-10 h-10 rounded-lg bg-blue-600 grid place-items-center">
            <Wallet className="w-5 h-5" />
          </div>
          <div className="mt-4 text-sm text-slate-300">Saldo total</div>
          <div className="mt-1 font-display text-3xl font-bold" data-testid="total-balance">
            {formatEUR(summary?.total_balance || 0)}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            En {connections.length} cuenta{connections.length === 1 ? "" : "s"}
          </div>
        </Card>

        <Card className="p-5 stat-card">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 grid place-items-center">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="mt-4 text-sm text-slate-500">Ingresos 30 días</div>
          <div className="mt-1 font-display text-2xl font-bold text-slate-900" data-testid="incomes-30d">
            {formatEUR(summary?.incomes_30d || 0)}
          </div>
        </Card>

        <Card className="p-5 stat-card">
          <div className="w-10 h-10 rounded-lg bg-rose-50 grid place-items-center">
            <TrendingDown className="w-5 h-5 text-rose-600" />
          </div>
          <div className="mt-4 text-sm text-slate-500">Gastos 30 días</div>
          <div className="mt-1 font-display text-2xl font-bold text-slate-900" data-testid="expenses-30d">
            {formatEUR(summary?.expenses_30d || 0)}
          </div>
        </Card>
      </div>

      {/* Bank cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {connections.map((c) => (
          <Card
            key={c.id}
            className="p-5 overflow-hidden relative"
            data-testid={`bank-conn-${c.id}`}
          >
            <div
              className="absolute top-0 right-0 w-32 h-32 opacity-10 -mr-8 -mt-8 rounded-full"
              style={{ background: c.institution_color }}
            />
            <div className="flex items-start justify-between gap-4 relative">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="w-12 h-12 rounded-xl grid place-items-center text-white font-bold text-lg shrink-0"
                  style={{ background: c.institution_color }}
                >
                  {c.institution_name.charAt(0)}
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-slate-900 truncate">
                    {c.institution_name}
                  </div>
                  <div className="text-xs text-slate-500 font-mono truncate">
                    {c.iban}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => refresh(c.id)}
                  data-testid={`refresh-${c.id}`}
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => disconnect(c.id, c.institution_name)}
                  className="text-rose-500"
                  data-testid={`disconnect-${c.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="mt-6 relative">
              <div className="text-xs text-slate-500 uppercase tracking-widest">Saldo actual</div>
              <div className="font-display text-3xl font-bold text-slate-900 mt-1">
                {formatEUR(c.balance)}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Badge variant="secondary" className="text-[10px]">
                  <ShieldCheck className="w-3 h-3 mr-1" /> PSD2 · Consentimiento activo
                </Badge>
                {c.mode === "demo" && (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] hover:bg-amber-100">
                    Modo demo
                  </Badge>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Categories pie */}
      {summary?.categories?.length > 0 && (
        <Card className="p-5">
          <div className="mb-2">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
              Análisis
            </div>
            <div className="font-display text-lg font-bold text-slate-900">
              Gastos por categoría (últimos 30 días)
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={summary.categories}
                  dataKey="amount"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                >
                  {summary.categories.map((_, i) => (
                    <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => formatEUR(v)}
                  contentStyle={{
                    background: "#0F172A",
                    border: "none",
                    borderRadius: 8,
                    color: "#F8FAFC",
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Transactions */}
      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
              Extracto bancario
            </div>
            <div className="font-display text-lg font-bold text-slate-900">
              Movimientos ({filtered.length})
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="pl-9 h-9 w-48"
                data-testid="tx-search"
              />
            </div>
            <Select value={filterCat} onValueChange={setFilterCat}>
              <SelectTrigger className="w-44 h-9" data-testid="tx-cat-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas las categorías</SelectItem>
                {categoriesList.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-500">
            No hay movimientos que coincidan con los filtros.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((t) => (
              <div
                key={t.id}
                className="py-3 flex items-center gap-4"
                data-testid={`tx-${t.id}`}
              >
                <div
                  className={`w-9 h-9 rounded-full grid place-items-center shrink-0 ${
                    t.amount > 0 ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"
                  }`}
                >
                  {t.amount > 0 ? (
                    <ArrowDownRight className="w-4 h-4" />
                  ) : (
                    <ArrowUpRight className="w-4 h-4" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {t.description}
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                    <span>{formatDate(t.booking_date)}</span>
                    {t.category && (
                      <span className="px-1.5 py-0.5 bg-slate-100 rounded text-[10px]">
                        {t.category}
                      </span>
                    )}
                    {t.matched_invoice_number && (
                      <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded text-[10px] flex items-center gap-1">
                        <Link2 className="w-2.5 h-2.5" /> {t.matched_invoice_number}
                      </span>
                    )}
                  </div>
                </div>
                <div
                  className={`font-medium text-sm ${
                    t.amount > 0 ? "text-emerald-600" : "text-slate-900"
                  }`}
                >
                  {t.amount > 0 ? "+" : ""}
                  {formatEUR(t.amount)}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <ConnectDialog
        open={connectOpen}
        onOpenChange={setConnectOpen}
        institutions={institutions}
        selectedInst={selectedInst}
        setSelectedInst={setSelectedInst}
        onConnect={connect}
        connecting={connecting}
      />
    </div>
  );
}

function ConnectDialog({
  open,
  onOpenChange,
  institutions,
  selectedInst,
  setSelectedInst,
  onConnect,
  connecting,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Conectar un banco</DialogTitle>
        </DialogHeader>
        {connecting ? (
          <div className="py-12 text-center">
            <Loader2 className="w-12 h-12 text-blue-600 mx-auto animate-spin" />
            <p className="mt-4 font-display font-bold text-lg text-slate-900">
              Conectando con tu banco...
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Estableciendo conexión segura vía PSD2 (Open Banking).
            </p>
            <p className="text-xs text-slate-400 mt-4">
              En modo real serías redirigido a tu banco para autorizar el acceso.
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-600">
              Selecciona tu banco. Verás tu saldo y movimientos en unos segundos.
            </p>
            <div className="max-h-80 overflow-y-auto -mx-1 px-1 grid grid-cols-2 gap-2">
              {institutions.map((b) => (
                <button
                  key={b.id}
                  onClick={() => setSelectedInst(b.id)}
                  className={`p-3 rounded-lg border text-left transition ${
                    selectedInst === b.id
                      ? "border-blue-600 bg-blue-50 ring-1 ring-blue-200"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`bank-option-${b.id}`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-9 h-9 rounded-lg grid place-items-center text-white font-bold shrink-0"
                      style={{ background: b.color }}
                    >
                      {b.name.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {b.name}
                      </div>
                      <div className="text-[10px] text-slate-500 font-mono">
                        {b.bic}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="text-[11px] text-slate-500 bg-slate-50 p-3 rounded border border-slate-200">
              <ShieldCheck className="w-3 h-3 inline mr-1 text-emerald-600" />
              Sólo lectura. Nunca tendremos acceso para hacer transferencias. El consentimiento PSD2 dura 90 días y puedes revocarlo en cualquier momento.
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button
                onClick={onConnect}
                disabled={!selectedInst}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="confirm-connect-btn"
              >
                Conectar
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
